// Per-section data loading for the onboarding analytics dashboard (CWA-159).
//
// There are eleven endpoints and each one scans three tables in full, so a
// single combined loading gate would make the screen as slow as its slowest
// section and as fragile as its most likely failure. Instead every section owns
// its own request, loading flag, error and retry (FR-034), and the page composes
// them. Follows useTargetGroupAnalytics, including the `ignore` flag that
// discards a stale response and the refresh key that re-runs without duplicating
// the fetch logic.

import { useCallback, useEffect, useRef, useState } from "react";
import { AnalyticsError } from "../../../../types/onboardingAnalytics";

/** Classify a failure, because they are not equally actionable. */
export const classifyError = (err: any): AnalyticsError => {
  const status = err?.response?.status;
  // Not an administrator. Retrying can never succeed, so the frame must not
  // offer a retry button (FR-035a).
  if (status === 401 || status === 403) {
    return {
      kind: "forbidden",
      message: "You don't have permission to view this.",
    };
  }
  // A parameter the API rejected. Its own message names raw enum values and
  // Kotlin validation text, so it is never shown (FR-017).
  if (status === 400) {
    return {
      kind: "invalid",
      message: "That filter combination isn't valid. Adjust it and try again.",
    };
  }
  return { kind: "transient", message: "Couldn't load this section." };
};

export interface SectionState<T> {
  data: T | null;
  loading: boolean;
  error: AnalyticsError | null;
  /** Re-request this section alone. */
  retry: () => void;
}

interface Options {
  /**
   * Whether this section should load at all. Sections behind a tab stay false
   * until the tab is first opened, so the initial load only fetches what an
   * agent can actually see.
   */
  enabled?: boolean;
  /**
   * Bumped by the page's refresh control to re-run every enabled section.
   */
  refreshKey?: number;
  /**
   * Values that change what is being asked for (a month range, a filter). A
   * change re-requests; an unchanged value does not.
   */
  deps?: unknown[];
}

/**
 * One section's data.
 *
 * The in-flight guard is the reason this is a hook rather than an inline effect:
 * a refresh while a request is outstanding must not stack a second one
 * (FR-036), and React 18's development double-invoke would otherwise fire two
 * requests per section on every mount.
 */
export function useAnalyticsSection<T>(
  fetcher: () => Promise<T>,
  { enabled = true, refreshKey = 0, deps = [] }: Options = {}
): SectionState<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<AnalyticsError | null>(null);
  const [retryKey, setRetryKey] = useState(0);
  // Internal re-run trigger, bumped when a request that arrived during another
  // one has to be honoured after it.
  const [tick, setTick] = useState(0);

  const inFlight = useRef(false);
  const pending = useRef(false);
  // Held in a ref so a caller can pass an inline arrow without re-triggering
  // the effect on every render.
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  useEffect(() => {
    if (!enabled) return undefined;

    // Never two concurrent requests for one section (FR-036) — but a refresh
    // or a filter change that lands mid-flight must not be LOST either, so it
    // is remembered and run once the outstanding request settles.
    if (inFlight.current) {
      pending.current = true;
      return undefined;
    }

    let ignore = false;
    inFlight.current = true;
    setLoading(true);
    setError(null);

    fetcherRef
      .current()
      .then((result) => {
        if (!ignore) {
          setData(result);
          setLoading(false);
        }
      })
      .catch((err) => {
        if (!ignore) {
          console.error("Onboarding analytics section failed:", err);
          setData(null);
          setError(classifyError(err));
          setLoading(false);
        }
      })
      .finally(() => {
        inFlight.current = false;
        if (pending.current) {
          pending.current = false;
          setTick((t) => t + 1);
        }
      });

    return () => {
      ignore = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, refreshKey, retryKey, tick, ...deps]);

  const retry = useCallback(() => setRetryKey((k) => k + 1), []);

  return { data, loading, error, retry };
}
